import { EventListenerMap, IdEventListener, useIdEventListener } from "@/hooks/useEventListeners";
import { useServiceEventListener } from "@/hooks/useService";
import { ContentDelta } from "@bloomstack/apis/llmTypes";
import { LLMHistory, LLMHistoryContent } from "@bloomstack/apis/llmTypes";
import { ManagerEntryAddedEvent, RPC_EVENT_MANAGER_ENTRY_ADDED_IDENT, ManagerEntryRemovedEvent, RPC_EVENT_MANAGER_ENTRY_REMOVED_IDENT, ManagerEntryUpdatedEvent, RPC_EVENT_MANAGER_ENTRY_UPDATED_IDENT, ManagerEntriesBulkRemovedEvent, RPC_EVENT_MANAGER_ENTRIES_BULK_REMOVED_IDENT, ManagerContentAddedEvent, RPC_EVENT_MANAGER_CONTENT_ADDED_IDENT, ManagerContentUpdatedEvent, RPC_EVENT_MANAGER_CONTENT_UPDATED_IDENT, ManagerContentRemovedEvent, RPC_EVENT_MANAGER_CONTENT_REMOVED_IDENT, ManagerStreamDeltaEvent, RPC_EVENT_MANAGER_STREAM_DELTA_IDENT } from "@bloomstack/apis/services/llmDataStream";
import { createContext, FC, useContext, useRef, useCallback, useMemo } from "react";
import type { FlatContent, ToolExecutionData } from "@/types/streamTypes";
import type { ContentResolver } from "@/hooks/useContentLookup";
import { generateContentId } from "@/utils/llmTransform";

export interface IStreamEvents extends EventListenerMap {
  // Entry events (broadcast: streamId, pointed: streamId/entryId)
  entryAdded: (streamId: string, entry: LLMHistory) => void;
  entryUpdated: (streamId: string, entry: LLMHistory) => void;
  entryDeleted: (streamId: string, entryId: string) => void;

  // Content events (broadcast: streamId, pointed: streamId/contentId)
  contentAdded: (streamId: string, content: LLMHistoryContent) => void;
  contentUpdated: (streamId: string, content: LLMHistoryContent) => void;
  contentDeleted: (streamId: string, entryId: string, contentId: string) => void;

  // Streaming delta (broadcast: streamId, pointed: streamId/contentId)
  streamDelta: (delta: ContentDelta) => void;
}

export interface DataStreamEventBusContextValue {
  events: IdEventListener<IStreamEvents>;
  getContent: (contentId: string) => FlatContent | undefined;
  setContent: (contentId: string, content: FlatContent) => void;
  releaseContent: (contentId: string) => void;
  resolvers: Map<FlatContent['type'], ContentResolver>;
}

export const dataStreamEventBusContext = createContext<DataStreamEventBusContextValue | null>(null);

export const useDataStreamEventBus = (throwIfMissing = true): DataStreamEventBusContextValue => {
  const context = useContext(dataStreamEventBusContext);
  if (!context && throwIfMissing) {
    throw new Error("useDataStreamEventBus must be used within a DataStreamEventBusProvider");
  }

  return context as DataStreamEventBusContextValue;
}

export type DataStreamEventBusProviderProps = {
  children: React.ReactNode;
}

export const DataStreamEventBusProvider: FC<DataStreamEventBusProviderProps> = (props) => { 
  const events = useIdEventListener<IStreamEvents>();
  const contentMapRef = useRef<Map<string, FlatContent>>(new Map());

  // Type-specific content resolvers (custom caching behavior per content type)
  const resolversRef = useRef<Map<FlatContent['type'], ContentResolver>>(new Map([
    // tool_execution: synthetic content that needs merge logic
    // Both tool_use and tool_result arrive separately and may be in either source
    ['tool_execution', (dexieContent, cacheContent, eventBus) => {
      // Neither available
      if (!dexieContent && !cacheContent) return undefined;
      
      // Only one available - return it
      if (!dexieContent) return cacheContent;
      if (!cacheContent) return dexieContent;
      
      // Both available - merge the data
      const dexieData = dexieContent.data as ToolExecutionData;
      const cacheData = cacheContent.data as ToolExecutionData;
      
      // Status priority: error > complete > streaming > pending
      const statusPriority: Record<ToolExecutionData['status'], number> = {
        pending: 0,
        streaming: 1,
        complete: 2,
        error: 3,
      };
      
      // Merge: take non-undefined values, prefer the more complete/advanced state
      const mergedData: ToolExecutionData = {
        toolUse: cacheData.toolUse ?? dexieData.toolUse,
        toolResult: cacheData.toolResult ?? dexieData.toolResult,
        status: statusPriority[cacheData.status] >= statusPriority[dexieData.status]
          ? cacheData.status
          : dexieData.status,
        startTime: Math.min(
          cacheData.startTime ?? Infinity,
          dexieData.startTime ?? Infinity
        ),
        endTime: cacheData.endTime ?? dexieData.endTime,
        toolUseEntryId: cacheData.toolUseEntryId ?? dexieData.toolUseEntryId,
        toolResultEntryId: cacheData.toolResultEntryId ?? dexieData.toolResultEntryId,
      };
      
      // Fix Infinity startTime edge case
      if (mergedData.startTime === Infinity) {
        mergedData.startTime = Date.now();
      }
      
      // Determine winner by arrivedAt for the base properties (not data)
      const dexieArrived = dexieContent.arrivedAt ?? -Infinity;
      const cacheArrived = cacheContent.arrivedAt ?? -Infinity;
      const winner = cacheArrived >= dexieArrived ? cacheContent : dexieContent;
      
      // Return merged content with base from winner
      const merged: FlatContent = {
        ...winner,
        data: mergedData,
        // Keep the latest arrivedAt
        arrivedAt: Math.max(dexieArrived, cacheArrived) !== -Infinity
          ? Math.max(dexieArrived, cacheArrived)
          : undefined,
      };
      
      // If merge is complete (has both toolUse and toolResult), release cache
      if (mergedData.toolUse && mergedData.toolResult) {
        eventBus.releaseContent(cacheContent.contentId);
      }
      
      return merged;
    }],
  ]));

  const getContent = useCallback((contentId: string): FlatContent | undefined => {
    return contentMapRef.current.get(contentId);
  }, []);

  const setContent = useCallback((contentId: string, content: FlatContent): void => {
    contentMapRef.current.set(contentId, content);
  }, []);

  const releaseContent = useCallback((contentId: string): void => {
    const content = contentMapRef.current.get(contentId);
    contentMapRef.current.delete(contentId);
  }, []);

  useServiceEventListener<ManagerEntryAddedEvent>(
    RPC_EVENT_MANAGER_ENTRY_ADDED_IDENT,
    (eventStreamId: string, entry: LLMHistory) => {
      events.emit[eventStreamId].entryAdded(eventStreamId, entry);
      events.emit[`${eventStreamId}/${entry.id}`].entryAdded(eventStreamId, entry);
    },
    [events]
  );
  
  useServiceEventListener<ManagerEntryUpdatedEvent>(
    RPC_EVENT_MANAGER_ENTRY_UPDATED_IDENT,
    (eventStreamId: string, entry: LLMHistory) => {
      events.emit[eventStreamId].entryUpdated(eventStreamId, entry);
      events.emit[`${eventStreamId}/${entry.id}`].entryUpdated(eventStreamId, entry);
    },
    [events]
  );

  useServiceEventListener<ManagerEntryRemovedEvent>(
    RPC_EVENT_MANAGER_ENTRY_REMOVED_IDENT,
    (eventStreamId: string, entryId: string) => {
      events.emit[eventStreamId].entryDeleted(eventStreamId, entryId);
      events.emit[`${eventStreamId}/${entryId}`].entryDeleted(eventStreamId, entryId);
    },
    [events]
  );

  useServiceEventListener<ManagerEntriesBulkRemovedEvent>(
    RPC_EVENT_MANAGER_ENTRIES_BULK_REMOVED_IDENT,
    (eventStreamId: string, entryIds: string[]) => {
      for (const entryId of entryIds) {
        events.emit[eventStreamId].entryDeleted(eventStreamId, entryId);
        events.emit[`${eventStreamId}/${entryId}`].entryDeleted(eventStreamId, entryId);
      }
    },
    [events]
  );
  
  useServiceEventListener<ManagerContentAddedEvent>(
    RPC_EVENT_MANAGER_CONTENT_ADDED_IDENT,
    (eventStreamId: string, entryId: string, content: LLMHistoryContent) => {
      events.emit[eventStreamId].contentAdded(eventStreamId, { ...content, entryId });
      events.emit[`${eventStreamId}/${content.id}`].contentAdded(eventStreamId, { ...content, entryId });
    },
    [events]
  );
  
  useServiceEventListener<ManagerContentUpdatedEvent>(
    RPC_EVENT_MANAGER_CONTENT_UPDATED_IDENT,
    (eventStreamId: string, entryId: string, content: LLMHistoryContent) => {
      events.emit[eventStreamId].contentUpdated(eventStreamId, { ...content, entryId });
      events.emit[`${eventStreamId}/${content.id}`].contentUpdated(eventStreamId, { ...content, entryId });
    },
    [events]
  );
  
  useServiceEventListener<ManagerContentRemovedEvent>(
    RPC_EVENT_MANAGER_CONTENT_REMOVED_IDENT,
    (eventStreamId: string, entryId: string, contentId: string) => {
      events.emit[eventStreamId].contentDeleted(eventStreamId, entryId, contentId);
      events.emit[`${eventStreamId}/${contentId}`].contentDeleted(eventStreamId, entryId, contentId);
    },
    [events]
  );

  useServiceEventListener<ManagerStreamDeltaEvent>(
    RPC_EVENT_MANAGER_STREAM_DELTA_IDENT,
    (delta: ContentDelta) => {
      // Broadcast to stream-level listeners
      events.emit[delta.streamId].streamDelta(delta);

      // Generate content ID based on delta type for targeted listeners
      let generatedContentId: string;
      if (delta.type === 'tool_progress') {
        generatedContentId = generateContentId({ type: 'tool_progress', requestId: delta.requestId });
      } else {
        // string or binary delta
        generatedContentId = generateContentId({ type: 'regular', entryId: delta.entryId, contentIndex: delta.contentIndex });
      }

      events.emit[`${delta.streamId}/${generatedContentId}`].streamDelta(delta);
    },
    [events]
  );

  const contextValue = useMemo<DataStreamEventBusContextValue>(() => ({
    events,
    getContent,
    setContent,
    releaseContent,
    resolvers: resolversRef.current,
  }), [events, getContent, setContent, releaseContent]);

  return (
    <dataStreamEventBusContext.Provider value={contextValue}>
      {props.children}
    </dataStreamEventBusContext.Provider>
  );
}
